import {
  API_ROUTES,
  type FloorPlan,
  type FloorPlanListResponse,
  type FloorPlanResponse,
  type FloorPlanWriteRequest,
  type PlacementRefType,
  type PlanImageMediaType,
  type PlanPlacement,
  type PlanPlacementListResponse,
  type PlanPlacementWriteRequest,
} from '@oscom/shared';
import { apiGet } from './client';
import { jsonInit, sendAndUnwrap } from './send';

function planUrl(customerId: string, planId: string): string {
  return `${API_ROUTES.customerFloorPlans(customerId)}/${encodeURIComponent(planId)}`;
}

function placementUrl(
  customerId: string,
  planId: string,
  refType: PlacementRefType,
  refId: string,
): string {
  return `${planUrl(customerId, planId)}/placements/${encodeURIComponent(refType)}/${encodeURIComponent(refId)}`;
}

/** The raw image bytes with the detected media type; no multipart. */
function imageInit(file: Blob, mediaType: PlanImageMediaType, extra?: HeadersInit): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': mediaType, ...extra },
    body: file,
  };
}

export function listFloorPlans(
  customerId: string,
  locationId?: string,
): Promise<FloorPlanListResponse> {
  const query = locationId ? `?${new URLSearchParams({ locationId }).toString()}` : '';
  return apiGet<FloorPlanListResponse>(`${API_ROUTES.customerFloorPlans(customerId)}${query}`);
}

export async function uploadFloorPlan(
  customerId: string,
  input: { locationId: string; name: string; file: Blob; mediaType: PlanImageMediaType },
): Promise<FloorPlanResponse> {
  const query = new URLSearchParams({ locationId: input.locationId, name: input.name });
  const response = await sendAndUnwrap(
    `${API_ROUTES.customerFloorPlans(customerId)}?${query.toString()}`,
    imageInit(input.file, input.mediaType),
    'Hochladen',
  );
  return (await response.json()) as FloorPlanResponse;
}

/** Sends `If-Match` with the etag of the plan the user looked at. */
export async function replaceFloorPlanImage(
  customerId: string,
  plan: Pick<FloorPlan, 'id' | '_etag'>,
  file: Blob,
  mediaType: PlanImageMediaType,
): Promise<FloorPlanResponse> {
  const response = await sendAndUnwrap(
    `${planUrl(customerId, plan.id)}/image`,
    imageInit(file, mediaType, plan._etag ? { 'if-match': plan._etag } : undefined),
    'Ersetzen',
  );
  return (await response.json()) as FloorPlanResponse;
}

export async function saveFloorPlan(
  customerId: string,
  planId: string,
  write: FloorPlanWriteRequest,
): Promise<FloorPlan> {
  const response = await sendAndUnwrap(
    planUrl(customerId, planId),
    jsonInit('PUT', write),
    'Speichern',
  );
  return (await response.json()) as FloorPlan;
}

export async function deleteFloorPlan(customerId: string, planId: string): Promise<void> {
  await sendAndUnwrap(planUrl(customerId, planId), { method: 'DELETE' }, 'Löschen');
}

/**
 * Stores the small preview of the plan's CURRENT image (`assetId`). Created by
 * the browser after an upload; the API refuses it once the image was replaced
 * or already has one.
 */
export async function uploadFloorPlanThumbnail(
  customerId: string,
  planId: string,
  assetId: string,
  blob: Blob,
): Promise<FloorPlanResponse> {
  const response = await sendAndUnwrap(
    `${floorPlanImageUrl(customerId, planId, assetId)}/thumbnail`,
    { ...imageInit(blob, 'image/jpeg'), method: 'PUT' },
    'Vorschau speichern',
  );
  return (await response.json()) as FloorPlanResponse;
}

/** Images never change under their URL (a replacement is a new asset id). */
export function floorPlanImageUrl(customerId: string, planId: string, assetId: string): string {
  return `${planUrl(customerId, planId)}/image/${encodeURIComponent(assetId)}`;
}

/** For small previews: the thumbnail when there is one, else the full image. */
export function floorPlanPreviewUrl(
  customerId: string,
  plan: Pick<FloorPlan, 'id' | 'currentImage'>,
): string {
  const image = plan.currentImage;
  return floorPlanImageUrl(customerId, plan.id, image.thumbnail?.assetId ?? image.assetId);
}

export function listPlacements(
  customerId: string,
  planId: string,
  seriesId?: string,
): Promise<PlanPlacementListResponse> {
  const query = seriesId ? `?${new URLSearchParams({ seriesId }).toString()}` : '';
  return apiGet<PlanPlacementListResponse>(`${planUrl(customerId, planId)}/placements${query}`);
}

export async function savePlacement(
  customerId: string,
  planId: string,
  refType: PlacementRefType,
  refId: string,
  write: PlanPlacementWriteRequest,
): Promise<PlanPlacement> {
  const response = await sendAndUnwrap(
    placementUrl(customerId, planId, refType, refId),
    jsonInit('PUT', write),
    'Speichern',
  );
  return (await response.json()) as PlanPlacement;
}

export async function deletePlacement(
  customerId: string,
  planId: string,
  refType: PlacementRefType,
  refId: string,
): Promise<void> {
  await sendAndUnwrap(
    placementUrl(customerId, planId, refType, refId),
    { method: 'DELETE' },
    'Entfernen',
  );
}
